import { useEffect, useRef, useState } from 'react';
import { BehaviorSubject, debounceTime, Subscription, tap } from 'rxjs';

interface UseDebouncedStateProps<T> {
  state: T;
  time: number;
}

const useDebouncedState = <T,>(
  props: UseDebouncedStateProps<T>
): [T & { isDebouncing: boolean }] => {
  const { state, time } = props;
  const statePublisher = useRef(new BehaviorSubject<T>(state));
  const stateSubscriber = useRef(Subscription.EMPTY);
  const [debouncedState, setDebouncedState] = useState<T & { isDebouncing: boolean }>({
    ...state,
    isDebouncing: true,
  });

  useEffect(() => {
    stateSubscriber.current = statePublisher.current
      .pipe(
        tap(() => {
          setDebouncedState((prev) => ({ ...prev, isDebouncing: true }));
        }),
        debounceTime(time),
        tap((current) => {
          setDebouncedState({ ...current, isDebouncing: false });
        })
      )
      .subscribe();
    return () => {
      stateSubscriber.current?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    statePublisher.current.next(state);
  }, [state]);

  return [debouncedState];
};

export default useDebouncedState;
